import { ReadonlyVec3, vec2, vec3 } from "gl-matrix";

import type { AnchoredSvgData } from "features/engine2D/svgRenderer/types";

import { MarkerIcon, MarkerMarkupBadge } from ".";
import { MarkerBounds, MarkerCamera, MarkerScreenPosition } from "./types";

/**
 * The visible content width of the pin shape in viewBox coordinates.
 * Derived from the mask region in createPinShapedMarker (`x="0" width="36"`).
 * Used as the scaling reference so that `size` controls the rendered visual width of the pin.
 */
const PIN_CONTENT_WIDTH = 36;

/**
 * ViewBox dimensions of the pin SVG template. Must stay in sync with the
 * `viewBox="0 0 57 56"` attribute in createPinShapedMarker.
 */
const PIN_VIEWBOX_WIDTH = 57;
const PIN_VIEWBOX_HEIGHT = 56;

/**
 * The anchor offset for the default pin SVG, expressed in the viewBox coordinate space
 * (`viewBox="0 0 57 56"`).
 *
 * The pin's pointy tip sits at approximately (18, 54) in viewBox coordinates — read directly
 * from the converging chevron path points in createPinShapedMarker. This offset is the
 * negative of that tip position: vec2(-18, -54) shifts the SVG left by 18 units and up by
 * 54 units so the tip lands exactly on the 3D anchor point when using
 * `anchorOffsetCoordinateType: "viewBox"`.
 *
 * If the pin SVG shape or its viewBox ever changes, update this to match the new tip
 * coordinates (negated). An incorrect value causes the "drag shift" bug where a dropped
 * pin jumps away from the cursor's release position.
 */
export const DEFAULT_PIN_ANCHOR_OFFSET: vec2 = [-18, -54];

/**
 * Default pin size in pixels when rendered (width dimension).
 */
export const DEFAULT_PIN_SIZE = 34;

/**
 * Lightens a hex color by a given amount
 * @param hexColor Hex color string (e.g., "#2196F3")
 * @param amount Amount to lighten (0-1)
 */
export function lightenColor(hexColor: string, amount: number): string {
    // Remove # if present
    const hex = hexColor.replace("#", "");

    // Parse RGB
    const r = Number.parseInt(hex.substring(0, 2), 16);
    const g = Number.parseInt(hex.substring(2, 4), 16);
    const b = Number.parseInt(hex.substring(4, 6), 16);

    // Lighten
    const newR = Math.min(255, Math.floor(r + (255 - r) * amount));
    const newG = Math.min(255, Math.floor(g + (255 - g) * amount));
    const newB = Math.min(255, Math.floor(b + (255 - b) * amount));

    // Convert back to hex
    return `#${newR.toString(16).padStart(2, "0")}${newG.toString(16).padStart(2, "0")}${newB.toString(16).padStart(2, "0")}`;
}

/**
 * Darkens a hex color by a given amount
 * @param hexColor Hex color string (e.g., "#2196F3")
 * @param amount Amount to darken (0-1, where 0 = no change, 1 = black)
 * @returns Darkened hex color string
 */
export function darkenColor(hexColor: string, amount: number): string {
    // Remove # if present
    const hex = hexColor.replace("#", "");

    // Parse RGB
    const r = Number.parseInt(hex.substring(0, 2), 16);
    const g = Number.parseInt(hex.substring(2, 4), 16);
    const b = Number.parseInt(hex.substring(4, 6), 16);

    // Darken by moving towards black (0)
    const newR = Math.max(0, Math.round(r * (1 - amount)));
    const newG = Math.max(0, Math.round(g * (1 - amount)));
    const newB = Math.max(0, Math.round(b * (1 - amount)));

    // Convert back to hex
    return `#${newR.toString(16).padStart(2, "0")}${newG.toString(16).padStart(2, "0")}${newB.toString(16).padStart(2, "0")}`;
}

const PREFIX = "marker";
const SEPARATOR = ":";

/**
 * Creates a unique composite string ID from collection and marker IDs.
 * Encodes components to safely handle special characters (like colons).
 */
export function createCompositeId(collectionId: string, markerId: string): string {
    return `${PREFIX}${SEPARATOR}${encodeURIComponent(collectionId)}${SEPARATOR}${encodeURIComponent(markerId)}`;
}

/**
 * Parses a composite string ID back into collection and marker IDs.
 * Returns null if the format is invalid.
 */
export function parseCompositeId(compositeId: string): { collectionId: string; markerId: string } | null {
    const parts = compositeId.split(SEPARATOR);
    if (parts.length !== 3 || parts[0] !== PREFIX) {
        return null;
    }
    try {
        return {
            collectionId: decodeURIComponent(parts[1]),
            markerId: decodeURIComponent(parts[2]),
        };
    } catch {
        // Malformed percent-encoding; treat as invalid composite ID
        return null;
    }
}

function createScreenPositionDataforSvg(
    svgElement: Element,
    canvas: HTMLCanvasElement,
    markerPosition: ReadonlyVec3,
    camera: MarkerCamera,
): MarkerScreenPosition | undefined {
    const elementRect = svgElement.getBoundingClientRect();
    if (elementRect.width === 0 || elementRect.height === 0) {
        return undefined;
    }

    const canvasRect = canvas.getBoundingClientRect();
    const bounds: MarkerBounds = {
        top: elementRect.top - canvasRect.top,
        bottom: elementRect.bottom - canvasRect.top,
        left: elementRect.left - canvasRect.left,
        right: elementRect.right - canvasRect.left,
        width: elementRect.width,
        height: elementRect.height,
    };

    const x = bounds.left + bounds.width / 2;
    const y = bounds.top + bounds.height / 2;

    const distance = vec3.distance(camera.position as vec3, markerPosition as vec3);
    const range = camera.far - camera.near;
    const depth = range > 0 ? Math.max(0, Math.min(1, (distance - camera.near) / range)) : 0;

    return { x, y, depth, bounds };
}

export function resolveMarkerScreenPosition(
    markerElement: Element | null | undefined,
    compositeId: string,
    canvas: HTMLCanvasElement,
    markerPosition: ReadonlyVec3,
    camera: MarkerCamera,
): MarkerScreenPosition | undefined {
    const fromExplicit = markerElement
        ? createScreenPositionDataforSvg(markerElement, canvas, markerPosition, camera)
        : undefined;
    if (fromExplicit) {
        return fromExplicit;
    }

    const escapedId = compositeId.replace(/"/g, '\\"');
    const fresh = document.querySelector(`[data-id="${escapedId}"]`);
    if (!fresh) {
        return undefined;
    }
    return createScreenPositionDataforSvg(fresh, canvas, markerPosition, camera);
}

export function scheduleEnlargedMeasurement({
    isStillTarget,
    measure,
    onDelivered,
    deliver,
}: {
    isStillTarget?: () => boolean;
    measure: () => MarkerScreenPosition | undefined;
    onDelivered?: () => void;
    deliver: (enlargedBounds: MarkerBounds | undefined) => void;
}): { cancel: () => void } {
    let raf2: number | null = null;
    const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => {
            if (isStillTarget && !isStillTarget()) {
                return;
            }
            const measured = measure();
            onDelivered?.();
            deliver(measured?.bounds);
        });
    });

    return {
        cancel: () => {
            cancelAnimationFrame(raf1);
            if (raf2 !== null) {
                cancelAnimationFrame(raf2);
            }
        },
    };
}

// Options type for marker icons where event handlers (onClick/onPointerEnter/onPointerOut/onPointerDown) require an id
type IconOptions =
    | {
          color: string;
          size: number;
          opacity: number;
          useMarkupBadge: MarkerMarkupBadge;
          stroke: string;
          strokeWidth?: number;
          cursor?: string;
          id?: string | { collectionId: string; markerId: string };
          onClick?: never;
          onPointerEnter?: never;
          onPointerOut?: never;
          onPointerDown?: never;
      }
    | ({
          color: string;
          size: number;
          opacity: number;
          useMarkupBadge: MarkerMarkupBadge;
          stroke: string;
          strokeWidth?: number;
          cursor?: string;
          id: string | { collectionId: string; markerId: string }; // Required for interactive markers
      } & (
          | {
                onClick: (id: string, event: PointerEvent) => void;
                onPointerEnter?: (id: string, event: PointerEvent) => void;
                onPointerOut?: (id: string, event: PointerEvent) => void;
                onPointerDown?: (id: string, event: PointerEvent) => void;
            }
          | {
                onClick?: (id: string, event: PointerEvent) => void;
                onPointerEnter: (id: string, event: PointerEvent) => void;
                onPointerOut?: (id: string, event: PointerEvent) => void;
                onPointerDown?: (id: string, event: PointerEvent) => void;
            }
          | {
                onClick?: (id: string, event: PointerEvent) => void;
                onPointerEnter?: (id: string, event: PointerEvent) => void;
                onPointerOut: (id: string, event: PointerEvent) => void;
                onPointerDown?: (id: string, event: PointerEvent) => void;
            }
          | {
                onClick?: (id: string, event: PointerEvent) => void;
                onPointerEnter?: (id: string, event: PointerEvent) => void;
                onPointerOut?: (id: string, event: PointerEvent) => void;
                onPointerDown: (id: string, event: PointerEvent) => void;
            }
      ));

export function addIconToSvgData(
    data: AnchoredSvgData,
    icon: MarkerIcon | null,
    position: vec3,
    options: IconOptions,
): void {
    const {
        color,
        size,
        opacity,
        stroke,
        strokeWidth,
        cursor,
        id,
        onClick,
        onPointerEnter,
        onPointerOut,
        onPointerDown,
    } = options;

    // Convert structured ID to composite string for SVG renderer
    let compositeId: string | undefined;
    if (typeof id === "string") {
        compositeId = id;
    } else if (id) {
        compositeId = createCompositeId(id.collectionId, id.markerId);
    }

    if (!icon) {
        data.svgContents!.push({
            anchor: position,
            content: createPinShapedMarker(null, options),
            opacity: opacity,
            scale: 1,
            // Shifts the SVG so its tip (at x=18, y=54 in the 63×72 pin coordinate space)
            // aligns with the 3D anchor point. Prevents visual jump on drag-drop.
            anchorOffset: DEFAULT_PIN_ANCHOR_OFFSET,
            anchorOffsetCoordinateType: "viewBox",
            cursor: cursor,
            ...(compositeId !== undefined && {
                id: compositeId,
                onClick,
                onPointerEnter,
                onPointerOut,
                onPointerDown,
            }),
        });

        return;
    }

    if ("svgContent" in icon) {
        data.svgContents!.push({
            anchor: position,
            content: icon.svgContent,
            opacity: opacity,
            scale: 1,
            anchorOffset: icon.anchorOffset,
            // Preserve the caller-supplied coordinate type when present.
            // When absent, infer the default from the shape of svgContent:
            //   - Full <svg> element  → "viewBox" (matches the pre-existing implicit behaviour and
            //     is correct because the renderer can parse viewBox+dimensions for scaling).
            //   - Bare fragment (<g>, <path>, etc.) → "screen" (no <svg> root to parse, so the
            //     renderer's fallback 1:1 mapping is the only valid interpretation).
            anchorOffsetCoordinateType:
                icon.anchorOffsetCoordinateType ??
                (icon.svgContent.trimStart().startsWith("<svg") ? "viewBox" : "screen"),
            cursor: cursor,
            ...(compositeId !== undefined && {
                id: compositeId,
                onClick,
                onPointerEnter,
                onPointerOut,
                onPointerDown,
            }),
        });
    } else if ("shape" in icon) {
        switch (icon.shape) {
            case "pin":
                data.svgContents!.push({
                    anchor: position,
                    content: createPinShapedMarker(icon, options),
                    opacity: opacity,
                    scale: 1,
                    // Shifts the SVG so its tip (at x=18, y=54 in the pin's SVG viewBox coordinate
                    // space, `viewBox="0 0 57 56"`) aligns with the 3D anchor point. Prevents visual
                    // jump on drag-drop.
                    anchorOffset: DEFAULT_PIN_ANCHOR_OFFSET,
                    anchorOffsetCoordinateType: "viewBox",
                    cursor: cursor,
                    ...(compositeId !== undefined && {
                        id: compositeId,
                        onClick,
                        onPointerEnter,
                        onPointerOut,
                        onPointerDown,
                    }),
                });
                break;
            case "circle":
                data.ellipses!.push({
                    anchor: position,
                    radius: [size / 2, size / 2],
                    fill: color,
                    opacity: opacity,
                    stroke: stroke ?? "#000000",
                    strokeWidth: strokeWidth ?? 1,
                    cursor: cursor,
                    ...(compositeId !== undefined && {
                        id: compositeId,
                        onClick,
                        onPointerEnter,
                        onPointerOut,
                        onPointerDown,
                    }),
                });
                break;
            case "square":
                data.rects!.push({
                    anchor: position,
                    width: size,
                    height: size,
                    fill: color,
                    opacity: opacity,
                    stroke: stroke,
                    strokeWidth: strokeWidth,
                    cursor: cursor,
                    ...(compositeId !== undefined && {
                        id: compositeId,
                        onClick,
                        onPointerEnter,
                        onPointerOut,
                        onPointerDown,
                    }),
                });
                break;
        }
    }
}

function createPinShapedMarker(
    icon: MarkerIcon | null,
    options: { color: string; stroke: string; size: number; opacity: number; useMarkupBadge: MarkerMarkupBadge },
): string {
    const iconSvgString = icon && "icon" in icon ? icon.icon : "";
    // Choose color for the stroke from the icon or the options.
    const strokeColor = options.stroke;

    // Darken the color for the inner fill (0.7 = 70% darker, keeps 30% of original brightness)
    const innerFillColor = options.color;

    const scaleFactor = options.size / PIN_CONTENT_WIDTH;
    const width = PIN_VIEWBOX_WIDTH * scaleFactor;
    const height = PIN_VIEWBOX_HEIGHT * scaleFactor;

    const markupBadgeSvgString =
        options.useMarkupBadge === true
            ? `<g filter="url(#filter0_d_4013_519)">
                <rect x="25" y="8" width="20" height="20" rx="10" fill="#D2E2FF"/>
                <path d="M32.0801 23.3493L30.9375 22.119V15L33.2228 15L33.2228 22.119L32.0801 23.3493Z" fill="#0A1E40"/>
                <path d="M32.7279 12.5C33.0014 12.5 33.2232 12.7384 33.2237 13.0328L33.2258 14.1633H30.9405L30.9385 13.0349C30.9379 12.7397 31.1601 12.5 31.4343 12.5H32.7279Z" fill="#0A1E40"/>
                <path d="M38.8328 21.0587C38.8328 21.7 38.3119 22.2202 37.6689 22.2202H34.5261L33.4209 23.3233H37.6689C38.9216 23.3233 39.9375 22.3097 39.9375 21.0587C39.9375 19.8078 38.9216 18.7942 37.6689 18.7942H36.5248C36.1977 18.7942 35.9329 18.5296 35.9329 18.2037C35.9329 17.8778 36.1977 17.6132 36.5248 17.6132H37.3829C38.4776 17.6132 39.3654 16.7273 39.3654 15.6341V14.226H38.2607V15.6341C38.2607 16.1177 37.8679 16.5101 37.3829 16.5101H36.5248C35.588 16.5101 34.8282 17.2681 34.8282 18.2037C34.8282 19.1392 35.588 19.8972 36.5248 19.8972H37.6689C38.3119 19.8972 38.8328 20.4175 38.8328 21.0587Z" fill="#0A1E40"/>
                </g>`
            : ``;

    return `<svg width="${width}" height="${height}" viewBox="0 0 57 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                <mask id="path-1-outside-1_4013_519" maskUnits="userSpaceOnUse" x="0" y="13" width="36" height="44" fill="black">
                <rect fill="white" y="13" width="36" height="44"/>
                <path d="M18 15C26.8366 15 34 22.1634 34 31C34 38.4858 28.8587 44.7692 21.9149 46.5157C21.8428 46.5338 21.7819 46.5826 21.7493 46.6493L18.2246 53.8608C18.1332 54.0478 17.8668 54.0478 17.7754 53.8608L14.2497 46.6493C14.2171 46.5825 14.1562 46.5338 14.0842 46.5157C7.14078 44.7689 2 38.4855 2 31C2 22.1634 9.16344 15 18 15Z"/>
                </mask>

                <!-- Background -->
                <path d="M18 15C26.8366 15 34 22.1634 34 31C34 38.4858 28.8587 44.7692 21.9149 46.5157C21.8428 46.5338 21.7819 46.5826 21.7493 46.6493L18.2246 53.8608C18.1332 54.0478 17.8668 54.0478 17.7754 53.8608L14.2497 46.6493C14.2171 46.5825 14.1562 46.5338 14.0842 46.5157C7.14078 44.7689 2 38.4855 2 31C2 22.1634 9.16344 15 18 15Z" fill="${innerFillColor}"/>

                <!-- Stroke -->
                <path d="M14.2497 46.6493L16.0465 45.7709L14.2497 46.6493ZM14.0842 46.5157L13.5962 48.4553L14.0842 46.5157ZM18.2246 53.8608L16.4277 52.9826L18.2246 53.8608ZM17.7754 53.8608L19.5721 52.9824L17.7754 53.8608ZM21.9149 46.5157L21.427 44.5761L21.9149 46.5157ZM21.7493 46.6493L23.5461 47.5275L21.7493 46.6493ZM18 15V17C25.732 17 32 23.268 32 31H34H36C36 21.0589 27.9411 13 18 13V15ZM34 31H32C32 37.547 27.5035 43.0477 21.427 44.5761L21.9149 46.5157L22.4027 48.4553C30.2139 46.4906 36 39.4246 36 31H34ZM21.7493 46.6493L19.9524 45.7711L16.4277 52.9826L18.2246 53.8608L20.0214 54.7391L23.5461 47.5275L21.7493 46.6493ZM17.7754 53.8608L19.5721 52.9824L16.0465 45.7709L14.2497 46.6493L12.453 47.5277L15.9786 54.7393L17.7754 53.8608ZM14.0842 46.5157L14.5721 44.5761C8.49609 43.0475 4 37.5468 4 31H2H0C0 39.4242 5.78548 46.4902 13.5962 48.4553L14.0842 46.5157ZM2 31H4C4 23.268 10.268 17 18 17V15V13C8.05887 13 0 21.0589 0 31H2ZM14.2497 46.6493L16.0465 45.7709C15.7507 45.1657 15.2054 44.7355 14.5721 44.5761L14.0842 46.5157L13.5962 48.4553C13.107 48.3322 12.6836 47.9994 12.453 47.5277L14.2497 46.6493ZM18.2246 53.8608L16.4277 52.9826C17.0673 51.6739 18.9324 51.6738 19.5721 52.9824L17.7754 53.8608L15.9786 54.7393C16.8011 56.4217 19.1991 56.4216 20.0214 54.7391L18.2246 53.8608ZM21.9149 46.5157L21.427 44.5761C20.7936 44.7354 20.2483 45.1658 19.9524 45.7711L21.7493 46.6493L23.5461 47.5275C23.3156 47.9993 22.892 48.3322 22.4027 48.4553L21.9149 46.5157Z" fill="${strokeColor}" mask="url(#path-1-outside-1_4013_519)"/>

                ${iconSvgString}

                <!-- Markup badge -->
                ${markupBadgeSvgString}

                <defs>
                <filter id="filter0_d_4013_519" x="13" y="0" width="44" height="44" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                <feOffset dy="4"/>
                <feGaussianBlur stdDeviation="6"/>
                <feComposite in2="hardAlpha" operator="out"/>
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0"/>
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_4013_519"/>
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_4013_519" result="shape"/>
                </filter>
                </defs>
            </svg>
            `;
}
